import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { CalendarDays, MapPin, Sparkles, ChevronLeft, ChevronRight, CheckCircle2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { schedulingApi, shiftSwapApi } from "../../services/schedulingApi";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ShiftSwapRequestModal } from "../../components/ShiftSwapRequestModal";
import { ShiftSwapRequestsList } from "../../components/ShiftSwapRequestsList";
import { useLocalization } from "../../hooks/useLocalization";
import { useAuth } from "../../hooks/useAuth";

const ACTIVE_SWAP_STATUSES = new Set(["PENDING_REPLACEMENT_RESPONSE", "REPLACEMENT_ACCEPTED", "PENDING_MANAGER_APPROVAL", "REVALIDATION_REQUIRED"]);

const ShiftCard = ({ shift, t, swappable, hasActiveSwap, onRequestSwap }) => {
  const start = dayjs(`${shift.date} ${shift.start_time}`);
  const end = shift.crosses_midnight ? dayjs(`${shift.date} ${shift.end_time}`).add(1, "day") : dayjs(`${shift.date} ${shift.end_time}`);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`my-schedule-shift-${shift.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-gray-900">{start.format("ddd, MMM D")}</p>
          <p className="text-sm text-gray-600">
            {shift.start_time} - {shift.end_time}
            {shift.crosses_midnight && <span className="text-xs text-amber-600 font-semibold ml-1.5" data-testid={`cross-midnight-badge-${shift.id}`}>({t("mySchedule.endsOn", { date: end.format("MMM D") })})</span>}
          </p>
        </div>
        {shift.is_qualifying_shift_template && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid={`qualifying-shift-badge-${shift.id}`}>
            <Sparkles size={11} /> {t("mySchedule.qualifyingShift")}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><MapPin size={12} /> {shift.store_location}</p>
      {shift.role && <p className="text-xs text-gray-500 mt-0.5">{shift.role}</p>}
      {shift.break_minutes > 0 && <p className="text-xs text-gray-500 mt-0.5">{shift.break_minutes} {t("mySchedule.breakMinutes")}</p>}
      {shift.manager_note && <p className="text-xs text-gray-500 mt-1.5 italic">"{shift.manager_note}"</p>}
      {swappable && (
        hasActiveSwap ? (
          <p className="text-xs text-blue-600 font-medium mt-2.5 flex items-center gap-1" data-testid={`swap-in-progress-label-${shift.id}`}><Repeat size={12} /> {t("shiftSwap.swapInProgress")}</p>
        ) : (
          <button
            data-testid={`request-swap-btn-${shift.id}`}
            onClick={() => onRequestSwap(shift)}
            className="flex items-center gap-1.5 mt-2.5 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50"
          >
            <Repeat size={13} /> {t("shiftSwap.requestSwapButton")}
          </button>
        )
      )}
    </div>
  );
};

export default function MySchedulePage() {
  const { t, language } = useLocalization();
  const { user } = useAuth();
  dayjs.locale(language === "ja" ? "ja" : "en");
  const [assignments, setAssignments] = useState([]);
  const [acknowledgedIds, setAcknowledgedIds] = useState(new Set());
  const [monthCursor, setMonthCursor] = useState(dayjs().format("YYYY-MM"));
  const [acking, setAcking] = useState(null);
  const [swapRequests, setSwapRequests] = useState([]);
  const [swapModalShift, setSwapModalShift] = useState(null);
  const [responding, setResponding] = useState(null);

  const load = () => {
    Promise.all([schedulingApi.mySchedule(), schedulingApi.myAcknowledgments(), shiftSwapApi.myRequests()]).then(([sched, acks, swaps]) => {
      setAssignments(sched.data);
      setAcknowledgedIds(new Set(acks.data.filter((a) => a.acknowledged_at).map((a) => a.schedule_id)));
      setSwapRequests(swaps.data);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const activeSwapAssignmentIds = useMemo(
    () => new Set(swapRequests.filter((r) => ACTIVE_SWAP_STATUSES.has(r.status)).map((r) => r.original_assignment_id)),
    [swapRequests]
  );

  const monthShifts = useMemo(
    () => assignments.filter((a) => a.date.startsWith(monthCursor)).sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time)),
    [assignments, monthCursor]
  );
  const today = dayjs().format("YYYY-MM-DD");
  const weekEnd = dayjs().add(7, "day").format("YYYY-MM-DD");
  const thisWeek = monthShifts.filter((a) => a.date >= today && a.date <= weekEnd);
  const later = monthShifts.filter((a) => a.date > weekEnd);
  const past = monthShifts.filter((a) => a.date < today);

  const pendingScheduleIds = [...new Set(assignments.map((a) => a.schedule_id))].filter((id) => !acknowledgedIds.has(id));

  const handleAcknowledge = async (scheduleId) => {
    setAcking(scheduleId);
    try {
      await schedulingApi.acknowledgeSchedule(scheduleId);
      toast.success(t("mySchedule.acknowledged"));
      load();
    } catch (err) {
      toast.error(t("mySchedule.acknowledgeError"));
    } finally {
      setAcking(null);
    }
  };

  const handleRespond = async (requestId, accept) => {
    setResponding(requestId);
    try {
      await shiftSwapApi.respond(requestId, accept);
      toast.success(accept ? t("shiftSwap.acceptedToast") : t("shiftSwap.declinedToast"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("shiftSwap.respondError"));
    } finally {
      setResponding(null);
    }
  };

  const handleCancel = async (requestId) => {
    setResponding(requestId);
    try {
      await shiftSwapApi.cancel(requestId);
      toast.success(t("shiftSwap.cancelledToast"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("shiftSwap.cancelError"));
    } finally {
      setResponding(null);
    }
  };

  const renderShiftCard = (s) => (
    <ShiftCard
      key={s.id} shift={s} t={t}
      swappable={s.date >= today}
      hasActiveSwap={activeSwapAssignmentIds.has(s.id)}
      onRequestSwap={setSwapModalShift}
    />
  );

  return (
    <div className="space-y-5" data-testid="my-schedule-page">
      <PageHelpBanner pageKey="my-schedule" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.mySchedule")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><CalendarDays size={20} /> {t("mySchedule.title")}</h2>
        <p className="text-sm text-gray-500">{t("mySchedule.subtitle")}</p>
      </div>

      {pendingScheduleIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-2" data-testid="my-schedule-ack-banner">
          <p className="text-sm font-semibold text-amber-800">{t(pendingScheduleIds.length > 1 ? "mySchedule.ackBannerMany" : "mySchedule.ackBannerOne", { count: pendingScheduleIds.length })}</p>
          {pendingScheduleIds.map((id) => (
            <button
              key={id}
              data-testid={`acknowledge-schedule-btn-${id}`}
              disabled={acking === id}
              onClick={() => handleAcknowledge(id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 disabled:opacity-50"
            >
              <CheckCircle2 size={13} /> {acking === id ? t("mySchedule.acknowledging") : t("mySchedule.acknowledgeSchedule")}
            </button>
          ))}
        </div>
      )}

      <ShiftSwapRequestsList requests={swapRequests} myEmployeeId={user?.employee_id} onRespond={handleRespond} onCancel={handleCancel} responding={responding} />

      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-2">
        <button data-testid="my-schedule-prev-month" onClick={() => setMonthCursor(dayjs(monthCursor + "-01").subtract(1, "month").format("YYYY-MM"))} className="p-1.5 rounded-full hover:bg-gray-100"><ChevronLeft size={16} /></button>
        <p className="text-sm font-semibold text-gray-800" data-testid="my-schedule-month-label">{dayjs(monthCursor + "-01").format("MMMM YYYY")}</p>
        <button data-testid="my-schedule-next-month" onClick={() => setMonthCursor(dayjs(monthCursor + "-01").add(1, "month").format("YYYY-MM"))} className="p-1.5 rounded-full hover:bg-gray-100"><ChevronRight size={16} /></button>
      </div>

      {monthShifts.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-8" data-testid="my-schedule-empty-state">{t("mySchedule.empty")}</p>
      )}

      {thisWeek.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{t("mySchedule.thisWeek")}</p>
          {thisWeek.map(renderShiftCard)}
        </div>
      )}
      {later.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{t("mySchedule.laterThisMonth")}</p>
          {later.map(renderShiftCard)}
        </div>
      )}
      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{t("mySchedule.earlierThisMonth")}</p>
          {past.map((s) => <ShiftCard key={s.id} shift={s} t={t} swappable={false} />)}
        </div>
      )}

      <ShiftSwapRequestModal shift={swapModalShift} open={!!swapModalShift} onClose={() => setSwapModalShift(null)} onSuccess={load} />
    </div>
  );
}

